import { icons } from '@chillibean/component-library/legacy';
import { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';

import Accordion, { AccordionToggleHeader, AccordionCollapse } from '../../../../../accordion/Accordion';
import HeroBox from '../../../../../HeroBox/HeroBox';
import Button from '../../../../../primitives/form/button/Button';
import CheckboxField from '../../../../../primitives/form/CheckboxField/CheckboxField';
import Rearrangeable from '../../../../../Rearrangeable/Rearrangeable';
import {
  setFieldValue,
  setActiveAccordion,
  setRearrangePosition,
  removeField,
  setInactiveAccordion,
} from '../../redux/actions/formTemplateActions';
import { validateTemplateFieldSets } from '../../utils/formTemplateValidation';
import FormTemplateFieldTypeManager from '../container/FormTemplateFieldTypeManager';
import AddAFieldDisplay from './AddAFieldDisplay/AddAFieldDisplay';

export function AddAField(props) {
  const {
    formTemplate,
    setFieldValue,
    addAField,
    setActiveAccordion,
    activeAccordion,
    setRearrangePosition,
    removeField,
    setInactiveAccordion,
    validateAllFields,
    readOnly,
    isFieldBeingDisabled,
  } = props;

  const draggedItem = useRef(null);
  const activeDragOverIndex = useRef(null);
  const [dragStart, setDragStart] = useState(-1);
  const [fieldsetFocus, setFieldsetFocus] = useState({
    fieldId: null,
    isFocused: false,
  });
  const [scrollToView, setScrollToView] = useState(false);
  const addAFieldRef = useRef(null);

  const checkHandleOnChange = (event, position, field) => {
    setFieldValue(position, field, event.target.checked);
  };

  const onDragStart = (e, index) => {
    draggedItem.current = formTemplate.fields[index];
    e.dataTransfer.effectAllowed = 'all';
    e.dataTransfer.dropEffect = 'move';
    e.dataTransfer.setData('text/html', e.target.parentNode);
  };

  const onDragOver = (e, index) => {
    e.preventDefault();
    if (!draggedItem.current) return;
    setDragStart(index);

    if (activeDragOverIndex.current !== index) {
      setInactiveAccordion([]);
      activeDragOverIndex.current = index;
      setRearrangePosition(formTemplate.fields, draggedItem.current, index);
    }
  };

  const onDragEnd = () => {
    draggedItem.current = null;
    setDragStart(-1);
  };

  const heroBoxClass = (id, index) => {
    if (!activeAccordion.includes(id) && dragStart === index) {
      return 'addAField__close addAField__active-drag';
    }
    if (!activeAccordion.includes(id)) {
      return 'addAField__close';
    }
    return '';
  };

  const onCloseClick = (id) => {
    removeField(id);
  };

  const showRemoveField = (persisted, fieldId) => {
    if (persisted || !activeAccordion.includes(fieldId)) return false;
    return readOnly ? false : formTemplate.fields.length > 1;
  };

  useEffect(() => {
    document.addEventListener('click', handleOutOfFocus);
    return () => {
      document.removeEventListener('click', handleOutOfFocus);
    };
  }, []);

  const isAccordionActive = (fieldId) => {
    return activeAccordion.includes(fieldId);
  };

  const handleHeroboxOnMouseEnter = (fieldId) => {
    if (!isAccordionActive(fieldId) || fieldsetFocus.isFocused) return false;
    setFieldsetFocus({ ...fieldsetFocus, fieldId });
  };

  function handleOutOfFocus(e) {
    if (isAddAFieldComponentFocused(e)) return false;
    setFieldsetFocus({ ...fieldsetFocus, isFocused: false });
  }

  const isAddAFieldComponentFocused = (e) => {
    if (addAFieldRef.current && addAFieldRef.current.contains(e.target)) return true;
    return false;
  };

  const handleHeroboxOnClick = (e, fieldId) => {
    const isInputActive = e.target.tagName.toLowerCase() === 'input' && !e.target.disabled;

    setFieldsetFocus({
      ...fieldsetFocus,
      fieldId: fieldId,
      isFocused: isInputActive,
    });
  };

  const isDisplayBoxShadow = (fieldId) => {
    return (
      (activeAccordion.length === 1 && isAccordionActive(fieldId)) ||
      (isAccordionActive(fieldsetFocus.fieldId) && fieldsetFocus.fieldId === fieldId)
    );
  };

  return (
    <div className="addAField" ref={addAFieldRef}>
      {formTemplate.fields !== undefined &&
        formTemplate.fields.map((fieldValues, index) => (
          <Rearrangeable
            key={index}
            draggable={!(readOnly || formTemplate.fields.length === 1 || activeAccordion.includes(fieldValues.id))}
            handleDragOver={(e) => onDragOver(e, index)}
            handleDragEnd={() => onDragEnd()}
            handleDragStart={(e) => onDragStart(e, index)}
          >
            <HeroBox
              scrollToView={formTemplate.fields.length - 1 === index ? scrollToView : false}
              displayBoxShadow={isDisplayBoxShadow(fieldValues.id)}
              cssClass={heroBoxClass(fieldValues.id, index)}
              showClose={showRemoveField(fieldValues.persisted, fieldValues.id)}
              handleClose={() => onCloseClick(fieldValues.id)}
              handleMouseEnter={() => handleHeroboxOnMouseEnter(fieldValues.id)}
              handleClick={(e) => handleHeroboxOnClick(e, fieldValues.id)}
            >
              <Accordion
                disable={false}
                activeAccordionIndex={activeAccordion.includes(fieldValues.id) ? fieldValues.id : -1}
                accordionIndex={fieldValues.id}
                icon={{
                  isSVG: true,
                  icon: icons.circleChevronUp,
                }}
                displayHeaderOnOpen
                customFunction={() => {
                  if (!activeAccordion.includes(fieldValues.id)) {
                    setActiveAccordion([fieldValues.id]);
                  } else {
                    setInactiveAccordion(fieldValues.id);
                  }
                }}
              >
                <AccordionToggleHeader>
                  <div className="addAField__display-wrapper" autoComplete="off" noValidate>
                    <AddAFieldDisplay fieldValues={fieldValues} isActive={activeAccordion.includes(fieldValues.id)} />
                    <div className="addAField__display addAField__display--checkbox">
                      <div className="form__group">
                        <CheckboxField
                          showError={false}
                          errorMessage=""
                          fieldID={`display_${fieldValues.position}_title`}
                          fieldIdentifier="active"
                          fieldName=""
                          fieldRequired={false}
                          optionValue=""
                          value=""
                          handleChange={(event) => {
                            isFieldBeingDisabled && isFieldBeingDisabled(fieldValues);
                            checkHandleOnChange(event, fieldValues.position, 'enabled');
                          }}
                          disabled={readOnly}
                          checked={fieldValues.enabled}
                        />
                      </div>
                    </div>
                  </div>
                </AccordionToggleHeader>
                <AccordionCollapse>
                  <form
                    className={`form ${!fieldValues.enabled ? 'addAField__disabled' : ''}`}
                    autoComplete="off"
                    noValidate
                  >
                    <FormTemplateFieldTypeManager fieldValues={fieldValues} validateAllFields={validateAllFields} />
                  </form>
                </AccordionCollapse>
              </Accordion>
            </HeroBox>
          </Rearrangeable>
        ))}
      {!readOnly && (
        <Button
          name="ADD A FIELD"
          actionType="button"
          disable={validateTemplateFieldSets(formTemplate).isError}
          buttonType="secondary"
          handleClick={() => {
            setScrollToView(false);
            setTimeout(() => {
              setScrollToView(true);
            }, 250);
            addAField();
          }}
          dataTestId="add-a-field-button"
        />
      )}
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    activeAccordion: state.formTemplateReducer.activeAccordion,
    readOnly: state.formTemplateUIReducer.readOnly,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setFieldValue: (position, fieldName, fieldValue) => {
      dispatch(setFieldValue(position, fieldName, fieldValue));
    },
    setActiveAccordion: (value) => {
      dispatch(setActiveAccordion(value));
    },
    setInactiveAccordion: (value) => {
      dispatch(setInactiveAccordion(value));
    },
    setRearrangePosition: (rearrangeFields, draggedItem, drugOverIndex) => {
      dispatch(setRearrangePosition(rearrangeFields, draggedItem, drugOverIndex));
    },
    removeField: (id) => {
      dispatch(removeField(id));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AddAField);
